import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import PostOnboardingRitual from './PostOnboardingRitual';

interface OnboardingProps {
  onComplete: () => void;
}

type ObjectiveChoice = 'massa' | 'secar' | 'condicionamento' | 'mental';
type ExperienceLevel = 'iniciante' | 'intermediario' | 'avancado';

export default function Onboarding({ onComplete }: OnboardingProps) {
  const { user } = useAuth();

  // UI config - Estética modo caverna
  const ACCENT_RED = '#E53935';
  const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';
  const BG_DARK = '#0D0D0D';


  // Questionnaire state (8 perguntas)
  const [step, setStep] = useState(1); // 1..8
  const totalSteps = 7;
  const [saving, setSaving] = useState(false);
  const [showRitual, setShowRitual] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Estados principais do onboarding
  const [objective, setObjective] = useState<ObjectiveChoice>('massa');
  const [daysPerWeek, setDaysPerWeek] = useState<number>(4);
  const [experience, setExperience] = useState<ExperienceLevel>('intermediario');
  const [dietStatus, setDietStatus] = useState<'desregrada' | 'regular' | 'dieta'>('regular');

  const [trainingTime, setTrainingTime] = useState<'manha' | 'tarde' | 'noite' | 'qualquer'>('manha');
  const [motivation, setMotivation] = useState<string>('');
  const [mentalState, setMentalState] = useState<string>('');

  // Verificar se já completou
  useEffect(() => {
    const checkCompletion = () => {
      const localCompleted = localStorage.getItem('shapex:onboarding');
      if (localCompleted) {
        // Se já completou, verificar se é um "redo" (não temos prop para isso, mas assumimos que se o componente montou e tem dados, pode ser um redo ou erro)
        // Mas para garantir "run once", se já tiver dados e não for explicitamente um reset, poderíamos pular.
        // Como o usuário pediu "executado somente uma vez", vamos confiar que o pai controla isso, 
        // mas aqui garantimos que se o ritual terminar, salvamos a flag.
      }
    };
    checkCompletion();
  }, []);

  // Salvamento automático de progresso
  useEffect(() => {
    if (!user) return;

    // Carregar progresso salvo
    const savedProgress = localStorage.getItem('shapex:onboarding_progress');
    if (savedProgress) {
      try {
        const data = JSON.parse(savedProgress);
        if (data.userId === user.id) {
          setStep(data.step || 1);
          setObjective(data.objective || 'massa');
          setDaysPerWeek(data.daysPerWeek || 4);
          setExperience(data.experience || 'intermediario');
          setDietStatus(data.dietStatus || 'regular');

          setTrainingTime(data.trainingTime || 'manha');
          setMotivation(data.motivation || '');
          setMentalState(data.mentalState || '');
        }
      } catch (error) {
        console.error('Erro ao carregar progresso:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const progressData = {
      userId: user.id,
      step,
      objective,
      daysPerWeek,
      experience,
      dietStatus,

      trainingTime,
      motivation,
      mentalState,
      lastSaved: new Date().toISOString()
    };

    localStorage.setItem('shapex:onboarding_progress', JSON.stringify(progressData));
  }, [step, objective, daysPerWeek, experience, dietStatus, trainingTime, motivation, mentalState, user]);

  const next = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setStep((s) => Math.min(totalSteps, s + 1));
      setIsTransitioning(false);
    }, 150);
  };

  const prev = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setStep((s) => Math.max(1, s - 1));
      setIsTransitioning(false);
    }, 150);
  };

  const mapDaysChoice = (days: number): '1-2' | '3-4' | '5-6' | '7' => {
    if (days <= 2) return '1-2';
    if (days <= 4) return '3-4';
    if (days <= 6) return '5-6';
    return '7';
  };

  const mapObjectiveSql = (obj: ObjectiveChoice): 'massa' | 'secar' | 'definir' => {
    if (obj === 'massa') return 'massa';
    if (obj === 'secar') return 'secar';
    return 'definir';
  };

  const mapGoalType = (obj: ObjectiveChoice): 'massa' | 'secar' | 'definir' | 'performance' => {
    if (obj === 'massa') return 'massa';
    if (obj === 'secar') return 'secar';
    if (obj === 'condicionamento') return 'definir';
    return 'performance';
  };

  const handleFinish = async () => {
    if (!user) return;
    setSaving(true);

    const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));
    try {
      const localPayload = {
        objective,
        daysPerWeek,
        experience,
        dietStatus,

        trainingTime,
        motivation,
        mentalState,
        completedAt: new Date().toISOString(),
      };
      localStorage.setItem('shapex:onboarding', JSON.stringify(localPayload));

      await supabase.from('user_onboarding').upsert({
        user_id: user.id,
        objective: mapObjectiveSql(objective),
        days_choice: mapDaysChoice(daysPerWeek),
        training_days_week: Math.min(7, daysPerWeek),

        diet_status: dietStatus,
        training_time: trainingTime,
        motivation: motivation || null,
        mental_state: mentalState || null,
      });

      await supabase.from('user_goals').upsert({
        user_id: user.id,
        goal_type: mapGoalType(objective),
        training_days_week: Math.min(7, daysPerWeek),
        experience_level: experience,

      });

      // Marcar que o questionário foi completado
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ has_completed_questionnaire: true })
        .eq('id', user.id);
      
      // Se o perfil não existe, criar com a flag
      if (updateError) {
        await supabase
          .from('profiles')
          .upsert({ 
            id: user.id,
            has_completed_questionnaire: true 
          }, {
            onConflict: 'id'
          });
      }

      await wait(1500);
      setSaving(false);
      localStorage.removeItem('shapex:onboarding_progress');
      setShowRitual(true);
    } catch (err) {
      console.error('Erro ao salvar onboarding:', err);
      setSaving(false);
    }
  };

  // Botão de opção padronizado (estilo "Ver substituições")
  const OptionButton = ({
    selected,
    onClick,
    label
  }: {
    selected: boolean;
    onClick: () => void;
    label: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full px-4 py-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] text-left"
      style={{
        backgroundColor: selected ? 'rgba(229, 57, 53, 0.1)' : 'transparent',
        borderColor: selected ? 'rgba(229, 57, 53, 0.3)' : BORDER_COLOR,
        color: selected ? ACCENT_RED : '#A1A1A1'
      }}
    >
      <span className={`text-sm font-bold ${selected ? 'text-[#E53935]' : 'text-white'}`}>
        {label}
      </span>
    </button>
  );

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
      {!showRitual && (
        <div className="container mx-auto px-4 pt-6 max-w-md">
          <header className="mb-8 pt-4">
            <h1 className="text-xl font-extrabold text-white mb-2" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontWeight: 800 }}>
              Prepare-se para a batalha mais importante: você contra você.
            </h1>
            <p className="text-[#A1A1A1] text-sm leading-relaxed">
              Responda com sinceridade. É aqui que sua guerra interna começa.
            </p>
          </header>

          {/* Barra de progresso */}
          <div className="mb-8">
            <div className="h-1.5 rounded-full overflow-hidden bg-[#1A1A1A]">
              <div
                className="h-full transition-all duration-500 ease-out rounded-full"
                style={{
                  width: `${Math.round((step / totalSteps) * 100)}%`,
                  backgroundColor: ACCENT_RED,
                  boxShadow: '0 0 10px rgba(229, 57, 53, 0.3)'
                }}
              />
            </div>
            <div className="mt-2 flex justify-between items-center">
              <span className="text-xs text-[#A1A1A1] font-medium">Etapa {step} de {totalSteps}</span>
              <span className="text-xs text-[#E53935] font-bold">{Math.round((step / totalSteps) * 100)}%</span>
            </div>
          </div>

          {/* Card da etapa */}
          <div className={`rounded-[18px] p-6 bg-[#141414] border shadow-lg transition-all duration-300 ease-out ${isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`} style={{ borderColor: BORDER_COLOR }}>
            {step === 1 && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white mb-2">
                    Qual é o seu objetivo principal?
                  </h2>
                  <p className="text-[#A1A1A1] text-xs">Selecione uma opção</p>
                </div>
                <div className="space-y-3">
                  {[
                    { value: 'massa', label: 'Ganhar massa muscular' },
                    { value: 'secar', label: 'Secar e definir' },
                    { value: 'condicionamento', label: 'Melhorar resistência e condicionamento' },
                    { value: 'mental', label: 'Performance mental e foco' },
                  ].map((opt) => (
                    <OptionButton
                      key={opt.value}
                      selected={objective === opt.value}
                      onClick={() => setObjective(opt.value as ObjectiveChoice)}
                      label={opt.label}
                    />
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white mb-2">
                    Quantos dias da semana você pode treinar?
                  </h2>
                  <p className="text-[#A1A1A1] text-xs">Escolha seu ritmo</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: 3, label: '3 dias' },
                    { v: 4, label: '4 dias' },
                    { v: 5, label: '5 dias' },
                    { v: 6, label: '6 dias' },
                  ].map((opt) => (
                    <OptionButton
                      key={opt.v}
                      selected={daysPerWeek === opt.v}
                      onClick={() => setDaysPerWeek(opt.v)}
                      label={opt.label}
                    />
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white mb-2">
                    Qual horário você costuma treinar?
                  </h2>
                  <p className="text-[#A1A1A1] text-xs">Selecione uma opção</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { v: 'manha', label: 'Manhã' },
                    { v: 'tarde', label: 'Tarde' },
                    { v: 'noite', label: 'Noite' },
                  ].map((opt) => (
                    <OptionButton
                      key={opt.v}
                      selected={trainingTime === opt.v}
                      onClick={() => setTrainingTime(opt.v as 'manha' | 'tarde' | 'noite' | 'qualquer')}
                      label={opt.label}
                    />
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white mb-2">
                    Qual é o seu nível atual de experiência?
                  </h2>
                  <p className="text-[#A1A1A1] text-xs">Selecione uma opção</p>
                </div>
                <div className="space-y-3">
                  {[
                    { v: 'iniciante', label: 'Iniciante' },
                    { v: 'intermediario', label: 'Intermediário' },
                    { v: 'avancado', label: 'Avançado' },
                  ].map((opt) => (
                    <OptionButton
                      key={opt.v}
                      selected={experience === opt.v}
                      onClick={() => setExperience(opt.v as ExperienceLevel)}
                      label={opt.label}
                    />
                  ))}
                </div>
              </div>
            )}

            {step === 5 && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white mb-2">
                    Como está sua alimentação atualmente?
                  </h2>
                  <p className="text-[#A1A1A1] text-xs">Selecione uma opção</p>
                </div>
                <div className="space-y-3">
                  {[
                    { v: 'desregrada', label: 'Desregrada' },
                    { v: 'regular', label: 'Regular, mas sem estratégia' },
                    { v: 'dieta', label: 'Já sigo uma dieta, mas quero melhorar' },
                  ].map((opt) => (
                    <OptionButton
                      key={opt.v}
                      selected={dietStatus === opt.v}
                      onClick={() => setDietStatus(opt.v as 'desregrada' | 'regular' | 'dieta')}
                      label={opt.label}
                    />
                  ))}
                </div>
              </div>
            )}

            {step === 6 && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white mb-2">
                    O que mais te motiva a mudar de verdade agora?
                  </h2>
                  <p className="text-[#A1A1A1] text-xs">Resposta curta do usuário</p>
                </div>
                <textarea
                  rows={4}
                  value={motivation}
                  onChange={(e) => setMotivation(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0D0D0D] text-white text-sm rounded-xl border focus:outline-none focus:border-[#E53935] transition-colors"
                  style={{ borderColor: BORDER_COLOR }}
                  placeholder="Ex: Melhorar minha saúde, ganhar confiança, foco total no trabalho..."
                />
              </div>
            )}

            {step === 7 && (
              <div>
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white mb-2">
                    Em uma palavra, como está seu estado mental hoje?
                  </h2>
                  <p className="text-[#A1A1A1] text-xs">Exemplo: "Focado", "Cansado", "Determinado"…</p>
                </div>
                <input
                  type="text"
                  value={mentalState}
                  onChange={(e) => setMentalState(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0D0D0D] text-white text-sm rounded-xl border focus:outline-none focus:border-[#E53935] transition-colors"
                  style={{ borderColor: BORDER_COLOR }}
                  placeholder="Digite 1 palavra"
                />
              </div>
            )}

            {/* Navegação */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={prev}
                  className="px-5 py-3 rounded-xl border transition-all duration-200 hover:scale-[1.02] flex items-center gap-2"
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: BORDER_COLOR,
                    color: '#A1A1A1'
                  }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm font-medium">Voltar</span>
                </button>
              ) : (
                <span />
              )}

              {step < totalSteps ? (
                <button
                  type="button"
                  onClick={next}
                  className="px-6 py-3 rounded-xl border transition-all duration-200 hover:scale-[1.02] flex items-center gap-2"
                  style={{
                    backgroundColor: 'rgba(229, 57, 53, 0.1)',
                    borderColor: 'rgba(229, 57, 53, 0.3)',
                    color: '#E53935'
                  }}
                >
                  <span className="text-sm font-bold">Continuar</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={saving}
                  className="px-6 py-3 rounded-xl border transition-all duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{
                    backgroundColor: '#E53935',
                    borderColor: '#E53935',
                    color: 'white',
                    boxShadow: '0 4px 20px rgba(229, 57, 53, 0.3)'
                  }}
                >
                  {saving ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-bold">Configurando...</span>
                    </span>
                  ) : (
                    <span className="text-sm font-bold">Estou pronto(a)</span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Novo Ritual Cinematográfico */}
      {showRitual && (
        <PostOnboardingRitual onComplete={onComplete} />
      )}
    </div>
  );
}
